// LoginForm.jsx
import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import PrimaryButton from "../../inputs/buttons/Button";
import FormikStyledField from "../../inputs/form/FormikStyledField";
import { BeatLoader } from "react-spinners";
import { useUser } from "../../../../contexts/UserContext";
import { loginUser, fetchProfiles, verifyToken } from "../../../../utils/api";
import CheckoutPasswordInput from "./CheckoutPasswordInput";
import { motion, useAnimation } from "framer-motion";
import { usePageSettings } from "../../../../contexts/PageSettingsContext";

// Validation schema
const validationSchema = Yup.object().shape({
	email: Yup.string().email("Invalid email").required("Required"),
	password: Yup.string().required("Required"),
});

const initialValues = {
	email: "",
	password: "",
};

const LoginForm = ({ closeModal }) => {
	const { translations } = usePageSettings();
	const { activateProfile, setUserProfiles } = useUser(); // Access updated UserContext
	const controls = useAnimation(); // Initialize animation controls

	// Handle login submission
	const handleLogin = async (values, setFieldError, setSubmitting) => {
		try {
			const token = await loginUser(values);
			const verifiedToken = await verifyToken(token);
			const fetchedProfiles = await fetchProfiles(verifiedToken);

			if (fetchedProfiles.length === 1) {
				activateProfile(fetchedProfiles[0]); // Activate single profile
				setUserProfiles(fetchedProfiles); // Persist single profile
			} else {
				activateProfile(fetchedProfiles[0]); // Default to the first profile
				setUserProfiles(fetchedProfiles); // Persist all profiles
			}
			closeModal(); // Close modal only on successful login
		} catch (error) {
			// Initialize default error message
			let errorMessage = "An unexpected error occurred. Please try again.";

			// Check if the error has a response from the server
			if (error.response) {
				const { status, data } = error.response;

				switch (status) {
					case 400:
						// Bad Request: Validation errors or malformed request
						errorMessage = data.message || "Invalid request. Please check your input.";
						break;
					case 401:
						// Unauthorized: Invalid credentials
						errorMessage = data.message || "Incorrect email or password.";
						break;
					case 403:
						// Forbidden: User doesn't have necessary permissions
						errorMessage = data.message || "You do not have access to this resource.";
						break;
					case 404:
						// Not Found: Endpoint not found
						errorMessage = data.message || "Requested resource not found.";
						break;
					case 500:
						// Internal Server Error: Server-side issue
						errorMessage = "Server error. Please try again later.";
						break;
					default:
						// Other status codes
						errorMessage = data.message || `Error ${status}: Please try again.`;
						break;
				}
			} else if (error.request) {
				// No response was received from the server
				errorMessage = "No response from server. Please check your network connection.";
			} else {
				// Error setting up the request
				errorMessage = error.message || errorMessage;
			}

			// Set specific field errors if applicable
			// For example, if the error is related to the email field
			if (error.response && error.response.data && error.response.data.field) {
				setFieldError(error.response.data.field, errorMessage);
			} else {
				// Set a general error message
				setFieldError("general", errorMessage);
			}

			// Trigger shake animation
			controls.start({
				x: [0, -10, 10, -10, 10, -10, 10, 0], // Shake sequence
				transition: { duration: 0.5 },
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={(values, { setSubmitting, setFieldError }) => {
				handleLogin(values, setFieldError, setSubmitting);
			}}
		>
			{({ errors, isSubmitting }) => (
				<Form className="flex flex-col gap-10">
					<div className="flex flex-col gap-7">
						<div className="flex flex-col gap-4">
							<FormikStyledField label="Email" name="email" type="email" required />
							<CheckoutPasswordInput label="Password" name="password" required />
						</div>
						<div className="flex w-full justify-end text-sm text-ipxon-magenta"
						>


							<a
								href={process.env.GATSBY_FORGOT_PASSWORD_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="text-sm text-ipxon-magenta"
							>
								{translations["CheckoutLoginFormForgotPassword"]}
							</a>
						</div>
					</div>
					{/* Display Status Message */}
					{errors.general && (
						<div className="flex flex-col bg-ipxon-light-gray items-center justify-center rounded-lg py-3.5 px-[50px]">
							<motion.div
								className="flex flex-col items-center"
								initial={{ opacity: 0, x: -10 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -10 }}
								transition={{ duration: 0.3 }}
							>
								<p className="ipxon-magenta-text-gradient text-sm font-normal">{errors.general}</p>
								<p className="text-ipxon-medium-dark-gray text-center text-sm font-normal">{translations["CheckoutLoginFormAddData"]}</p>
							</motion.div>
						</div>
					)}

					{/* Instruction Message */}

					{/* Animated Login Button */}
					<motion.div className="flex self-center">
						<PrimaryButton
							type="submit"
							disabled={isSubmitting}
							className="mt-3 rounded-full text-white px-4 py-2"
						>
							{isSubmitting ? <BeatLoader color="#d1d5db" size={8} /> : translations["CheckoutLoginLabel"]}

						</PrimaryButton>
					</motion.div>
				</Form>
			)}
		</Formik>
	);
};

export default LoginForm;
